/**
 * @license Copyright (c) 2003-2023, CKSource Holding sp. z o.o. All rights reserved.
 * For licensing, see LICENSE.md or https://ckeditor.com/legal/ckeditor-oss-license
 */
/**
 * @module video/videoutils
 */
import { Plugin } from '@ckeditor/ckeditor5-core';
import { findOptimalInsertionRange, isWidget, toWidget } from '@ckeditor/ckeditor5-widget';
import { determineVideoTypeForInsertionAtSelection } from './video/utils';
export default class VideoUtils extends Plugin {
    /**
     * @inheritDoc
     */
    static get pluginName() {
        return 'VideoUtils';
    }
    isVideo(modelElement) {
        return this.isInlineVideo(modelElement) || this.isBlockVideo(modelElement);
    }
    isInlineVideoView(element) {
        return !!element && element.is('element', 'video');
    }
    isBlockVideoView(element) {
        return !!element && element.is('element', 'figure') && element.hasClass('video');
    }
    insertVideo(attributes = {}, selectable = null, videoType = null) {
        const editor = this.editor;
        const model = editor.model;
        const selection = model.document.selection;
        // console.log('insert steppppppp');
        videoType = determineVideoTypeForInsertion(editor, selectable || selection, videoType);
        attributes = {
            ...Object.fromEntries(selection.getAttributes()),
            ...attributes
        };
        for (const attributeName in attributes) {
            if (!model.schema.checkAttribute(videoType, attributeName)) {
                delete attributes[attributeName];
            }
        }
        // console.log('complete insertion videoType', videoType);
        // console.log('complete insertion attributes', attributes);
        return model.change(writer => {
            const videoElement = writer.createElement(videoType, attributes);
            model.insertObject(videoElement, selectable, null, {
                setSelection: 'on',
                // If we want to insert a block image (for whatever reason) then we don't want to split text blocks.
                // This applies only when we don't have the selectable specified (i.e., we insert multiple block images at once).
                findOptimalPosition: !selectable && videoType != 'videoInline' ? 'auto' : undefined
            });
            // console.log('complete insertion videoElement', videoElement);
            // Inserting an image might've failed due to schema regulations.
            if (videoElement.parent) {
                return videoElement;
            }
            return null;
            // if ( !selectable && videoType !== 'videoInline' ) {
            //     selectable = findOptimalInsertionRange( selection, model );
            // }
            // model.insertContent( videoElement, selectable );
            // if ( videoElement.parent ) {
            //     writer.setSelection( videoElement, 'on' );
            //     return videoElement;
            // }
            // return null;
        });
        // console.log('complete insertion videoType', videoType);
        // console.log('complete insertion attributes', attributes);
    }
    getClosestSelectedVideoWidget(selection) {
        const selectionPosition = selection.getFirstPosition();
        if (!selectionPosition) {
            return null;
        }
        const viewElement = selection.getSelectedElement();
        if (viewElement && this.isVideoWidget(viewElement)) {
            return viewElement;
        }
        let parent = selectionPosition.parent;
        while (parent) {
            if (parent.is('element') && this.isVideoWidget(parent)) {
                return parent;
            }
            parent = parent.parent;
        }
        return null;
    }
    getClosestSelectedVideoElement(selection) {
        const selectedElement = selection.getSelectedElement();
        return this.isVideo(selectedElement) ? selectedElement : selection.getFirstPosition().findAncestor('videoBlock');
    }
    /**
     * Checks if video can be inserted at current model selection.
     *
     * @internal
     */
    isVideoAllowed() {
        const model = this.editor.model;
        const selection = model.document.selection;
        return isVideoAllowedInParent(this.editor, selection) && isNotInsideVideo(selection);
    }
    toVideoWidget(viewElement, writer) {
        writer.setCustomProperty('video', true, viewElement);
        return toWidget(viewElement, writer);
    }
    isVideoWidget(viewElement) {
        return !!viewElement.getCustomProperty('video') && isWidget(viewElement);
    }
    isBlockVideo(modelElement) {
        return !!modelElement && modelElement.is('element', 'videoBlock');
    }
    isInlineVideo(modelElement) {
        return !!modelElement && modelElement.is('element', 'videoInline');
    }
    findViewVideoElement(figureView) {
        if (this.isInlineVideoView(figureView)) {
            return figureView;
        }
        const editingView = this.editor.editing.view;
        for (const { item } of editingView.createRangeIn(figureView)) {
            if (this.isInlineVideoView(item)) {
                return item;
            }
        }
        //not sure
        return figureView;
    }
}
function isVideoAllowedInParent(editor, selection) {
    const videoType = determineVideoTypeForInsertion(editor, selection, null);
    if (videoType === 'videoBlock') {
        const parent = getInsertVideoParent(selection, editor.model);
        if (editor.model.schema.checkChild(parent, 'videoBlock')) {
            return true;
        }
    }
    else if (editor.model.schema.checkChild(selection.focus, 'videoInline')) {
        return true;
    }
    return false;
}
function isNotInsideVideo(selection) {
    return [...selection.focus.getAncestors()].every(ancestor => !ancestor.is('element', 'videoBlock'));
}
function getInsertVideoParent(selection, model) {
    const insertionRange = findOptimalInsertionRange(selection, model);
    const parent = insertionRange.start.parent;
    if (parent.isEmpty && !parent.is('element', '$root')) {
        return parent.parent;
    }
    return parent;
}
function determineVideoTypeForInsertion(editor, selectable, videoType) {
    const schema = editor.model.schema;
    const configVideoInsertType = editor.config.get('video.insert.type');
    if (!editor.plugins.has('VideoBlockEditing')) {
        return 'videoInline';
    }
    if (!editor.plugins.has('VideoInlineEditing')) {
        return 'videoBlock';
    }
    if (videoType) {
        return videoType;
    }
    if (configVideoInsertType === 'inline') {
        return 'videoInline';
    }
    if (configVideoInsertType === 'block') {
        return 'videoBlock';
    }
    if (selectable.is('selection')) {
        return determineVideoTypeForInsertionAtSelection(schema, selectable);
    }
    return schema.checkChild(selectable, 'videoInline') ? 'videoInline' : 'videoBlock';
}
//# sourceMappingURL=videoutils.js.map